import React from 'react';
import { cn } from '@/lib/utils';
import { DesktopNavBar } from '@/components/navigation/DesktopNavBar';
import { MobileNavBar } from '@/components/navigation/MobileNavBar';
import { AppFooter } from '@/components/common/AppFooter';

interface PageLayoutProps {
  children: React.ReactNode;
  /** Additional classes for the outer wrapper */
  className?: string;
  /** Classes applied to the full-width background wrapper */
  bgClassName?: string;
  /** Max width of the content area. Default: wide editorial spread. */
  maxWidth?: string;
  /** Whether to show DesktopNavBar (default true) */
  showDesktopNav?: boolean;
  /** Whether to show MobileNavBar (default true) */
  showMobileNav?: boolean;
  /** Whether to include bottom padding for mobile nav (default true) */
  mobileBottomPadding?: boolean;
  /** Accessible label for the main content area */
  ariaLabel?: string;
}

/**
 * Unified page layout wrapper.
 * Handles navbar offset, consistent padding, max-width, and mobile bottom spacing.
 */
export function PageLayout({
  children,
  className,
  bgClassName,
  maxWidth = 'max-w-[1520px]',
  showDesktopNav = true,
  showMobileNav = true,
  mobileBottomPadding = true,
  ariaLabel = 'Page content',
}: PageLayoutProps) {
  return (
    <div
      className={cn('gd-app gd-editorial min-h-screen bg-background text-foreground', bgClassName)}
      style={{ overscrollBehaviorY: 'contain' }}
    >
      {showDesktopNav && <DesktopNavBar />}

      <main
        id="main-content"
        role="main"
        aria-label={ariaLabel}
        className={cn(
          'mx-auto w-full px-5 pt-20 pb-6 md:px-8 md:pt-24 md:pb-8 lg:px-12',
          maxWidth,
          mobileBottomPadding && 'pb-24 md:pb-8',
          className
        )}
        style={{
          paddingBottom: mobileBottomPadding
            ? 'calc(6rem + env(safe-area-inset-bottom))'
            : undefined,
        }}
      >
        {children}
      </main>

      <div className={cn(mobileBottomPadding && 'pb-20 md:pb-0')}>
        <AppFooter />
      </div>

      {/* Mobile nav is always rendered so the auth-aware user hub (Sign Out /
          Delete Account) can never go missing on mobile. */}
      <MobileNavBar />
    </div>
  );
}
